/**
 * TypeScript Type Definitions for Global Properties API
 * 
 * This file contains all TypeScript interfaces and types for the Global Properties API endpoints.
 * 
 * @see docs/api/GLOBAL_PROPERTIES_API_GUIDE.md for complete API documentation
 */

// ============================================================================
// Property Types
// ============================================================================

export type PropertyType = 
  | 'string'
  | 'number'
  | 'integer'
  | 'float'
  | 'boolean'
  | 'date'
  | 'date_range'
  | 'currency'
  | 'duration'
  | 'url'
  | 'uuid'
  | 'uuid[]'
  | 'list';

// ============================================================================
// Global Visibility Properties
// ============================================================================

export interface GlobalVisibilityPropertiesResponse {
  properties: Record<string, GlobalVisibilityProperty>;
  last_updated: string; // ISO 8601 timestamp
}

export interface GlobalVisibilityProperty {
  value: string | number | boolean | null;
  artifact_id: string | null;
  artifact_name: string;
  artifact_item_id: string | null;
  item_name: string | null;
  updated_at: string | null; // ISO 8601 timestamp
}

// ============================================================================
// Property Definition
// ============================================================================

export interface PropertyDefinitionResponse {
  property_key: string;
  definition: PropertyDefinition;
}

// ============================================================================
// Property Summary (for table/list views with pre-computed counts)
// ============================================================================

export interface PropertySummary {
  property_key: string;
  property_type: string;
  description?: string | null;
  global_option_catalog_ref?: string | null;
  dependencies_count: number;
  dependents_count: number;
  has_calculation: boolean;
  has_validation: boolean;
  has_state_actions: boolean;
  option_catalog_name?: string | null;
}

export interface PropertyDefinition {
  property_key: string;
  property_type: PropertyType;
  description?: string;
  default_value?: any;
  input_source?: string;
  global_option_catalog_ref?: string; // UUID
  validation_rules?: ValidationRules;
  calculation_dependencies?: CalculationDependencies;
  change_type_definitions?: ChangeTypeDefinition[]; // Reusable change type definitions
  transition_changes?: TransitionChange[] | TransitionChangeReference[]; // New reference-based format or legacy inline format
  state_creation?: {
    created_in_state: string; // Standard state name from ENUM
    updated_in_states?: string[]; // Array of standard state names
  };
  // State actions as array format: [{state: "CREATED", flow_type: "operational", state_action_type: "System", ...}]
  state_actions?: StateActionEntry[];
  user_input_parameters?: UserInputParameters; // @deprecated - use transition_changes with change_type: "user_input" instead
  // Property-level configuration fields (defaults/templates)
  referenced_properties?: ReferencedProperty[];
  property_conditions?: {
    when?: string;
    required_if?: string;
  };
  resource_requirements?: {
    required_skills?: string[];
    resource_type?: 'PROCESS_RESOURCE' | 'USER' | 'SYSTEM';
    description?: string;
  };
}

// ============================================================================
// State Actions
// ============================================================================

// State Action Types
// Simplified 4-type model (ISSUE-104: standardized to 4 types)
// Old types are normalized via normalizeStateAction() function for backward compatibility
export type StateAction = 'System' | 'User' | 'Calculate' | 'Reference' | 'none';

// Alias for clarity (StateAction is now the 4-type model)
export type StateActionNew = StateAction;

// Helper function to normalize state action to new format
export function normalizeStateAction(action: StateAction | string): StateActionNew | 'none' {
  const normalized = typeof action === 'string' ? action.trim() : action;
  
  // Map old types to new types
  const mapping: Record<string, StateActionNew | 'none'> = {
    'system': 'System',
    'user': 'User',
    'calculate': 'Calculate',
    'update': 'User', // Update maps to User in new model
    'resolve': 'System', // Resolve maps to System in new model
    'reference': 'Reference',
    'System': 'System',
    'User': 'User',
    'Calculate': 'Calculate',
    'Reference': 'Reference',
    'none': 'none'
  };
  
  return mapping[normalized] || 'none';
}

// Helper to check if state action is valid new type
export function isValidStateActionNew(action: string): action is StateActionNew {
  return ['System', 'User', 'Calculate', 'Reference'].includes(action);
}

export interface StateActions {
  operational?: Record<string, StateAction>;
  improvement?: Record<string, StateAction>;
}

// New flat array structure for state_actions
export interface StateActionEntry {
  state: string;              // e.g., "ACTIVE", "CREATED"
  flow_type: 'operational' | 'improvement';
  state_action_type: StateAction;  // "System", "User", "Calculate", "Reference"
  flow_direction: FlowDirection;   // "both", "forward", "backward"
  change_type: string;             // References change_type_name
}

// Referenced property configuration (for property-level References tab)
export interface ReferencedProperty {
  property_key?: string;
  artifact_type?: string;
  artifact_item?: 'improvement' | 'operational';
  filter?: string;
  selection_method?: 'process' | 'user' | 'system';
  source?: 'same_artifact' | 'parent_artifact' | 'child_artifact' | 'related_artifact';
}

// Named reference configuration (stored in calculation_dependencies as reference1, reference2, etc.)
export interface ReferenceConfiguration {
  artifact_type?: string;
  artifact_item?: 'operational' | 'improvement';
  property_key?: string; // Single property key (legacy support)
  property_keys?: string[]; // Multiple property keys (new format)
  filter?: string;
  selection_method?: 'process' | 'user' | 'system';
  source?: 'same_artifact' | 'parent_artifact' | 'child_artifact' | 'related_artifact';
  reference_type?: 'simple' | 'transformed';
  description?: string;
}

// ============================================================================
// User Input Parameters
// ============================================================================

export interface UserInputParameters {
  label?: string;
  placeholder?: string;
  help_text?: string;
  input_type?: 'text' | 'number' | 'select' | 'date' | 'checkbox' | 'textarea';
  options?: Array<{ value: string; label: string }>; // For select type
  validation?: ValidationRules;
  input_mask?: string; // Input mask type (e.g., 'phone', 'email', 'address_street', 'custom')
  input_mask_pattern?: string; // Custom mask pattern (used when input_mask is 'custom')
}

// ============================================================================
// Validation Rules
// ============================================================================

export interface ValidationRules {
  type?: PropertyType;
  min?: number;
  max?: number;
  min_length?: number;
  max_length?: number;
  pattern?: string; // Regex pattern
  required?: boolean;
  enum?: any[];
  [key: string]: any; // Additional validation rules
}

export interface PropertyValidationRulesResponse {
  property_key: string;
  validation_rules: ValidationRules;
}

// ============================================================================
// Calculation Dependencies
// ============================================================================

export interface CalculationDependencies {
  type?: 'add' | 'subtract' | 'multiply' | 'divide' | 'sum' | 'average' | 'custom';
  source_properties?: Record<string, SourceProperty>;
  calculation_config?: Record<string, any>;
  // For list-type properties: store user input label, help text, default value, and reference
  user_input_label?: string;
  user_input_help_text?: string;
  user_input_default_value?: string | number | boolean;
  allow_multiple_selections?: boolean; // For list-type properties: allow selecting multiple values
  user_input_mask?: string; // Input mask type for string properties
  user_input_mask_pattern?: string; // Custom mask pattern (used when input_mask is 'custom')
  user_input?: {
    label?: string;
    help_text?: string;
    default_value?: string | number | boolean;
    allow_multiple_selections?: boolean;
    input_mask?: string; // Input mask type (e.g., 'phone', 'email', 'address_street', 'custom')
    input_mask_pattern?: string; // Custom mask pattern (used when input_mask is 'custom')
    display_format?: 'percentage' | 'decimal'; // Display format for numeric inputs: 'percentage' shows 0-100 with %, 'decimal' shows 0.0-1.0
    [key: string]: any;
  };
  reference?: string; // Reference ID (e.g., global_option_catalog_ref for list-type properties)
  reference_id?: string; // Alternative reference ID field
  // Property-level referenced properties (from References tab)
  referenced_properties?: ReferencedProperty[];
  // Boolean property conditions (true/false condition definitions)
  boolean_conditions?: BooleanConditions;
  [key: string]: any; // Allow additional properties for flexibility
}

export interface SourceProperty {
  property_key?: string; // Direct property key (legacy format)
  reference?: string; // Named reference configuration (e.g., 'reference1')
  property_index?: number; // Index into reference.property_keys array (0-based)
  operator?: string; // Operator role (e.g., 'numerator', 'denominator', 'operand1', etc.)
  source?: 'same_artifact' | 'parent_artifact' | 'child_artifact' | 'related_artifact';
  description?: string;
}

// ============================================================================
// Boolean Conditions
// ============================================================================

export interface BooleanStateCondition {
  artifact_item: 'current' | 'reference';
  reference_artifact_item?: string; // if artifact_item is 'reference'
  state: string; // e.g., 'defined'
}

export interface BooleanPropertyValueCondition {
  artifact_item: 'current' | 'reference';
  reference_artifact_item?: string; // if artifact_item is 'reference'
  property_key: string;
  operator: '=' | '!=' | '>' | '<' | '>=' | '<=';
  value: any;
}

export interface BooleanCondition {
  user_input?: boolean;
  state_condition?: BooleanStateCondition;
  property_value?: BooleanPropertyValueCondition[];
}

export interface BooleanConditions {
  true_condition?: BooleanCondition;
  false_condition?: BooleanCondition;
}

export interface PropertyDependenciesResponse {
  property_key: string;
  dependencies: PropertyDependency[];
}

export interface PropertyDependency {
  property_key: string;
  source: string;
  description?: string;
}

// ============================================================================
// State Availability
// ============================================================================

/**
 * @deprecated Use state_actions array format instead
 */
export interface StateAvailability {
  available_in_states?: string[];
  required_in_states?: string[];
  hidden_in_states?: string[];
  readonly_in_states?: string[];
}

export interface PropertyStateAvailabilityResponse {
  property_key: string;
  state_availability: StateAvailability;
}

// ============================================================================
// Transition Changes
// ============================================================================

// Flow direction for change types
export type FlowDirection = 'forward' | 'backward' | 'both';

// New change type structure (property-centric architecture)
export interface ChangeTypeDefinitionNew {
  change_type_name: string; // Matches state_action type (e.g., "User", "Calculate", "User2", "User3")
  state_action_type?: StateAction; // Indicates System, User, Calculate, or Reference (for organization/filtering)
  // Note: flow_direction removed - direction comes from state_actions
  // Type-specific configurations (mutually exclusive) - these override property-level defaults
  system?: {
    default_value?: any;
    reference_type?: 'improvement' | 'operational';
    system_type?: string; // e.g., "Period Generation" for dates
    system_parameters?: Record<string, any>;
  };
  user_input?: {
    input_fields?: InputFieldDefinition[];
  };
  calculate?: {
    calculation_type?: string; // Property-type specific (e.g., "Divide", "Record Count", "Assessment")
    transformation_logic?: any;
    source_properties?: Record<string, SourceProperty>;
    calculation_config?: Record<string, any>;
  };
  reference?: {
    artifact_type?: string;
    artifact_item?: 'improvement' | 'operational';
    filter?: string;
    reference_type?: 'simple' | 'transformed';
    filter_config?: Record<string, any>;
    selection_method?: 'process' | 'user' | 'system'; // How the referenced property is selected
    property_key?: string; // The property key being referenced
    source?: 'same_artifact' | 'parent_artifact' | 'child_artifact' | 'related_artifact'; // Source location
  };
  // Common configurations
  conditions?: {
    when?: string;
    required_if?: string;
  };
  validation?: ValidationRules;
  resource?: {
    required_skills?: string[];
    resource_type?: 'PROCESS_RESOURCE' | 'USER' | 'SYSTEM';
    description?: string;
  };
  dependencies?: string[];
}

// Legacy change type structure (backward compatibility)
export interface ChangeTypeDefinitionLegacy {
  name: string; // Unique identifier within property (e.g., "user_input_on_create", "calculate_revenue")
  change_type: 'set' | 'calculate' | 'clear' | 'update' | 'user_input' | 'copy_from_improvement';
  value?: any; // For set/update change type
  transformation_logic?: any; // For calculate change type
  update_logic?: any; // For update change type
  source_property?: string; // For copy_from_improvement change type
  input_fields?: InputFieldDefinition[]; // For user_input change type
  resource_requirements?: {
    required_skills: string[];
    resource_type: 'PROCESS_RESOURCE' | 'USER' | 'SYSTEM';
    description?: string;
  };
  conditions?: {
    when?: string;
    required_if?: string;
  };
  dependencies?: string[];
}

// Unified change type definition (supports both old and new structures)
export interface ChangeTypeDefinition extends Partial<ChangeTypeDefinitionNew>, Partial<ChangeTypeDefinitionLegacy> {
  // New structure fields (optional for backward compatibility)
  change_type_name?: string;
  state_action_type?: StateAction;
  flow_direction?: FlowDirection; // Kept for backward compatibility during migration, but not used in new structure
  system?: ChangeTypeDefinitionNew['system'];
  user_input?: ChangeTypeDefinitionNew['user_input'];
  calculate?: ChangeTypeDefinitionNew['calculate'];
  reference?: ChangeTypeDefinitionNew['reference'];
  validation?: ValidationRules;
  resource?: ChangeTypeDefinitionNew['resource'];
  
  // Legacy structure fields (optional for forward compatibility)
  name?: string;
  change_type?: 'set' | 'calculate' | 'clear' | 'update' | 'user_input' | 'copy_from_improvement';
  value?: any;
  transformation_logic?: any;
  update_logic?: any;
  source_property?: string;
  resource_requirements?: {
    required_skills: string[];
    resource_type: 'PROCESS_RESOURCE' | 'USER' | 'SYSTEM';
    description?: string;
  };
  conditions?: {
    when?: string;
    required_if?: string;
  };
  dependencies?: string[];
  
  // Shared fields
  input_fields?: InputFieldDefinition[];
}

// Helper to check if change type uses new structure
export function isNewChangeTypeStructure(changeType: ChangeTypeDefinition): changeType is ChangeTypeDefinitionNew {
  return changeType.change_type_name !== undefined;
}

// Helper to check if change type uses legacy structure
export function isLegacyChangeTypeStructure(changeType: ChangeTypeDefinition): changeType is ChangeTypeDefinitionLegacy {
  return changeType.name !== undefined && changeType.change_type !== undefined && changeType.change_type_name === undefined;
}

// Helper to get change type name (works with both structures)
export function getChangeTypeName(changeType: ChangeTypeDefinition): string {
  return changeType.change_type_name || changeType.name || '';
}

// Helper to normalize change type to new structure
export function normalizeChangeTypeToNew(changeType: ChangeTypeDefinition): ChangeTypeDefinitionNew | null {
  if (isNewChangeTypeStructure(changeType)) {
    return changeType as ChangeTypeDefinitionNew;
  }
  
  if (isLegacyChangeTypeStructure(changeType)) {
    // Convert legacy to new structure
    const newType: ChangeTypeDefinitionNew = {
      change_type_name: changeType.name,
      // Note: flow_direction removed from new structure - it comes from state_actions now
    };
    
    // Map legacy change_type to new structure
    switch (changeType.change_type) {
      case 'user_input':
        newType.user_input = {
          input_fields: changeType.input_fields || []
        };
        break;
      case 'calculate':
        newType.calculate = {
          transformation_logic: changeType.transformation_logic
        };
        break;
      case 'set':
      case 'update':
        newType.system = {
          default_value: changeType.value || changeType.update_logic
        };
        break;
      case 'copy_from_improvement':
        newType.reference = {
          artifact_type: changeType.source_property, // Map source_property to artifact_type
          reference_type: 'simple'
        };
        break;
    }
    
    if (changeType.conditions) {
      newType.conditions = changeType.conditions;
    }
    
    if (changeType.resource_requirements) {
      newType.resource = {
        required_skills: changeType.resource_requirements.required_skills,
        resource_type: changeType.resource_requirements.resource_type,
        description: changeType.resource_requirements.description
      };
    }
    
    if (changeType.dependencies) {
      newType.dependencies = changeType.dependencies;
    }
    
    return newType;
  }
  
  return null;
}

// New reference-based transition change structure (new format)
export interface TransitionChangeReference {
  change_type_name: string; // Name of the change type (e.g., "System", "User", "User2", "Calculate", "Reference")
  state_action_type: 'System' | 'User' | 'Calculate' | 'Reference'; // The state action type this change_type corresponds to
  // Reference fields (strings pointing to named configurations)
  user_input?: string; // Reference to named user_input configuration in calculation_dependencies (e.g., "user_input1")
  calculation?: string; // Reference to named calculation configuration in calculation_dependencies (e.g., "calculation1")
  reference?: string; // Reference to named reference configuration in calculation_dependencies (e.g., "reference1")
  condition?: string; // Reference to named condition configuration in validation_rules (e.g., "condition1")
  validation?: string; // Reference to named validation configuration in validation_rules (e.g., "validation1")
  resource?: string; // Reference to named resource configuration in validation_rules (e.g., "resource1")
}

// Legacy inline transition change structure (old format - backward compatibility)
export interface TransitionChange {
  property_key?: string; // Optional in global property context
  // Backend expects from_state and to_state (not transition string) - OLD FORMAT ONLY
  from_state?: string; // Source state name (e.g., "INITIALIZED", "CREATED") - OLD FORMAT ONLY
  to_state?: string; // Target state name (e.g., "DEFINED", "DRAFTED") - OLD FORMAT ONLY
  transition?: string; // Legacy: e.g., "INITIALIZED → DEFINED" (for backward compatibility, but backend uses from_state/to_state)
  state_action?: string; // Metadata only: e.g., "improvement.DEFINED" or "operational.CREATED" - DO NOT STORE
  state_action_type?: 'System' | 'User' | 'Calculate' | 'Reference' | 'none'; // Valid field: indicates the action type (should be stored)
  flow_direction?: 'forward' | 'backward' | 'both'; // Flow direction for the transition - OLD FORMAT ONLY
  change_type_ref?: string; // Name of ChangeTypeDefinition to reuse (if set, this overrides inline config)
  // If change_type_ref is not set, include inline configuration (for backward compatibility):
  // Valid change types: "System", "User", "Calculate", "Reference" (capitalized)
  change_type?: 'System' | 'User' | 'Calculate' | 'Reference' | 'set' | 'calculate' | 'clear' | 'update' | 'user_input' | 'copy_from_improvement'; // Backend expects capitalized, legacy lowercase for compatibility
  value?: any; // For set/update change type (legacy)
  transformation_logic?: any; // For calculate change type (legacy)
  update_logic?: any; // For update change type (legacy)
  source_property?: string; // For copy_from_improvement change type (legacy - use reference object instead)
  // Type-specific configuration objects (backend expects these nested objects) - OLD FORMAT ONLY
  system?: { // For System change type
    default_value?: any;
    system_type?: string;
    reference_type?: string;
  };
  user_input?: { // For User change type
    input_fields?: InputFieldDefinition[];
    input_type?: string;
    label?: string;
    default_value?: any;
    required?: boolean;
    help_text?: string;
    source?: string;
  };
  calculations?: { // For Calculate change type
    calculation_type?: string;
    transformation_logic?: any;
    source_properties?: any;
    formula?: string;
  };
  reference?: { // For Reference change type - contains reference configuration details
    artifact_type?: string;
    artifact_item?: 'improvement' | 'operational';
    property_key?: string; // Property key of the referenced property
    filter?: string;
    selection_method?: 'process' | 'user' | 'system';
    source?: 'same_artifact' | 'parent_artifact' | 'child_artifact' | 'related_artifact';
    reference_type?: 'simple' | 'transformed';
  };
  input_fields?: InputFieldDefinition[]; // For user_input change type (legacy - use user_input.input_fields instead)
  resource?: { // For resource requirements (backend expects 'resource' not 'resource_requirements')
    resource_type?: 'process' | 'user' | 'system';
    resource_skill?: string | null;
    description?: string;
  };
  resource_requirements?: { // Legacy format
    required_skills: string[]; // Array of skill IDs required to perform this transition
    resource_type: 'PROCESS_RESOURCE' | 'USER' | 'SYSTEM'; // Type of resource needed
    description?: string; // Why these skills/resources are needed
  };
  conditions?: {
    when?: string; // e.g., "is_improvement = TRUE"
    required_if?: string;
  };
  validation?: { // Validation rules for this transition
    required?: boolean;
    type_specific_validation?: any;
  };
  dependencies?: string[]; // Properties or artifacts this change depends on
}

// Type guard to check if transition change is reference-based (new format)
export function isReferenceBasedTransitionChange(tc: TransitionChange | TransitionChangeReference): tc is TransitionChangeReference {
  return 'change_type_name' in tc && !('from_state' in tc) && !('change_type' in tc && 'from_state' in tc);
}

// Type guard to check if transition change is inline-based (old format)
export function isInlineBasedTransitionChange(tc: TransitionChange | TransitionChangeReference): tc is TransitionChange {
  return 'from_state' in tc || ('change_type' in tc && !('change_type_name' in tc));
}

export interface InputFieldDefinition {
  name: string; // Maps to property_key or custom field name
  type: PropertyType; // "string" | "number" | "boolean" | "date" | "currency" | "duration" | "url" | "uuid" | "uuid[]" | "list"
  label: string; // Display label for the input field
  required: boolean;
  validation_rules?: {
    min?: number;
    max?: number;
    min_length?: number;
    max_length?: number;
    pattern?: string; // Regex pattern
    custom_validation?: string;
  };
  default_value?: any;
  help_text?: string;
  placeholder?: string;
  options?: Array<{ value: any; label: string }>; // For enumerated values
  input_mask?: string; // Input mask type (e.g., 'phone', 'email', 'address_street', 'custom')
  input_mask_pattern?: string; // Custom mask pattern (used when input_mask is 'custom')
}

// ============================================================================
// Property Validation
// ============================================================================

export interface PropertyValidationResponse {
  valid: boolean;
  artifact_item_id: string;
  validation_results: Record<string, PropertyValidationResult>;
  errors: string[];
}

export interface PropertyValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// ============================================================================
// Available Properties for State
// ============================================================================

export interface AvailablePropertiesResponse {
  artifact_id: string;
  state_name: string;
  available_properties: AvailableProperty[];
  required_properties: string[];
  readonly_properties: string[];
  hidden_properties: string[];
}

export interface AvailableProperty {
  property_key: string;
  is_required: boolean;
  is_readonly: boolean;
  is_hidden: boolean;
}

// ============================================================================
// Property Calculation
// ============================================================================

export interface CalculatePropertyRequest {
  transformation_logic?: TransformationLogic;
}

export interface TransformationLogic {
  type: 'add' | 'subtract' | 'multiply' | 'divide' | 'sum' | 'average' | 'custom';
  source_properties: Record<string, SourceProperty>;
  calculation_config?: Record<string, any>;
}

export interface CalculatePropertyResponse {
  property_key: string;
  artifact_item_id: string;
  calculated_value: any;
}

// ============================================================================
// Balance Sheet Validation
// ============================================================================

export interface BalanceSheetValidationResponse {
  balanced: boolean;
  assets: number;
  liabilities: number;
  equity: number;
  difference: number;
  errors: string[];
}

// ============================================================================
// Alignment Calculations
// ============================================================================

export interface ProductSituationAlignmentRequest {
  solution_properties: Record<string, any>;
  situation_properties: Record<string, any>;
}

export interface ProductSituationAlignmentResponse {
  alignment_percentage: number;
  solution_properties: Record<string, any>;
  situation_properties: Record<string, any>;
}

export interface ActorSituationAlignmentRequest {
  actor_properties: Record<string, any>;
}

export interface ActorSituationAlignmentResponse {
  alignment_percentage: number;
  actor_properties: Record<string, any>;
}

export interface DealScoreResponse {
  deal_score: number;
  product_situation_alignment: number;
  product_situation_value: number;
  actor_situation_alignment: number;
}

// ============================================================================
// Import Global Properties
// ============================================================================

export interface ImportGlobalPropertiesResponse {
  imported: number;
  errors: string[];
  warnings: string[];
}

// ============================================================================
// API Error Types
// ============================================================================

export interface ApiErrorResponse {
  detail: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public endpoint: string
  ) {
    super(`API Error (${status}): ${detail}`);
    this.name = 'ApiError';
  }
}

// ============================================================================
// Utility Types
// ============================================================================

export interface FormField {
  key: string;
  label: string;
  type: PropertyType;
  value: any;
  required: boolean;
  readonly: boolean;
  validationRules: ValidationRules;
}

export interface PropertyValuePair {
  property_key: string;
  value: any;
}

// ============================================================================
// Helper Type Guards
// ============================================================================

export function isPropertyType(value: string): value is PropertyType {
  const validTypes: PropertyType[] = [
    'string',
    'number',
    'integer',
    'float',
    'boolean',
    'date',
    'date_range',
    'currency',
    'duration',
    'url',
    'uuid',
    'uuid[]',
    'list'
  ];
  return validTypes.includes(value as PropertyType);
}

export function isValidUUID(uuid: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
}

